import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, AdminOverview, HoldMusicTrack, NumberRecord, StaffRecord } from "../lib/api";

const SERVICE_OPTIONS = [
  { value: "MEGAMILLIONSCLAIMS", label: "Mega Millions Claims" },
  { value: "MEGAFED", label: "Mega Reserve" },
  { value: "BOTH", label: "Both" }
];

const HOLD_SERVICE_OPTIONS = SERVICE_OPTIONS.filter((option) => option.value !== "BOTH");
const DEFAULT_HOLD_PROMPT =
  "Create calm, professional instrumental telephone hold music with smooth piano, subtle ambient textures and gentle transitions. No vocals. The music should be unobtrusive, reassuring and suitable for callers waiting to speak with a representative.";

type StaffEdit = {
  role: string;
  service: string;
  desktopAccess: boolean;
  sipExtension: string;
  outboundNumberIds: string[];
  forwardingNumber: string;
  forwardingNumberVerified: boolean;
  forwardingEnabled: boolean;
  forwardingConfirmationRequired: boolean;
};

function staffEditFromRecord(staff: StaffRecord): StaffEdit {
  const contexts = Array.isArray(staff.serviceContexts) ? staff.serviceContexts : [];
  return {
    role: staff.role || "agent",
    service:
      contexts.includes("MEGAMILLIONSCLAIMS") && contexts.includes("MEGAFED")
        ? "BOTH"
        : contexts.includes("MEGAFED")
          ? "MEGAFED"
          : "MEGAMILLIONSCLAIMS",
    desktopAccess: staff.desktopAccess !== false,
    sipExtension: staff.sipExtension || "",
    outboundNumberIds: Array.isArray(staff.outboundNumberIds) ? staff.outboundNumberIds : [],
    forwardingNumber: staff.forwardingNumber || "",
    forwardingNumberVerified: Boolean(staff.forwardingNumberVerified),
    forwardingEnabled: staff.forwardingEnabled !== false,
    forwardingConfirmationRequired: staff.forwardingConfirmationRequired !== false
  };
}

function serviceContextsFromValue(value: string) {
  return value === "BOTH" ? ["MEGAMILLIONSCLAIMS", "MEGAFED"] : [value];
}

export function AdminPage({ csrf }: { csrf: string }) {
  const [data, setData] = useState<AdminOverview | null>(null);
  const [staff, setStaff] = useState<StaffRecord[]>([]);
  const [numbers, setNumbers] = useState<NumberRecord[]>([]);
  const [routing, setRouting] = useState<{ campaigns: Record<string, unknown>[]; transfers: Record<string, unknown>[]; failures: Record<string, unknown>[] }>({ campaigns: [], transfers: [], failures: [] });
  const [holdTracks, setHoldTracks] = useState<HoldMusicTrack[]>([]);
  const [staffEdits, setStaffEdits] = useState<Record<string, StaffEdit>>({});
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [holdBusy, setHoldBusy] = useState("");
  const [holdForm, setHoldForm] = useState({
    serviceContext: "MEGAMILLIONSCLAIMS",
    prompt: DEFAULT_HOLD_PROMPT,
    durationMs: 30000,
    instrumental: true
  });
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    emailConfirm: "",
    password: "",
    role: "agent",
    service: "MEGAMILLIONSCLAIMS",
    sipExtension: ""
  });

  function load() {
    api.adminOverview().then(setData).catch((e) => setError(e.message));
    api.listNumbers().then((r) => setNumbers(r.numbers || [])).catch(() => {});
    api.adminRouting().then(setRouting).catch(() => {});
    api.listHoldMusic().then((r) => setHoldTracks(r.tracks || [])).catch(() => {});
    api.listStaff()
      .then((r) => {
        const rows = r.staff || [];
        setStaff(rows);
        setStaffEdits((prev) => {
          const next = { ...prev };
          for (const row of rows) {
            if (row.userId && !next[row.userId]) next[row.userId] = staffEditFromRecord(row);
          }
          return next;
        });
      })
      .catch(() => {});
  }

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setMsg("");
    setError("");
    const normalizedEmail = form.email.trim().toLowerCase();
    const normalizedEmailConfirm = form.emailConfirm.trim().toLowerCase();
    if (normalizedEmail !== normalizedEmailConfirm) {
      setError("Email addresses do not match.");
      return;
    }
    if (form.password.length < 12 || !/[a-z]/.test(form.password) || !/[A-Z]/.test(form.password) || !/[0-9]/.test(form.password)) {
      setError("Temporary password must be at least 12 characters and include uppercase, lowercase, and a number.");
      return;
    }
    setBusy(true);
    try {
      await api.createStaff(
        {
          fullName: form.fullName || form.email.split("@")[0],
          email: normalizedEmail,
          password: form.password,
          passwordConfirm: form.password,
          role: form.role,
          serviceContexts: serviceContextsFromValue(form.service),
          sipExtension: form.sipExtension.trim()
        },
        csrf
      );
      setMsg(`Created ${normalizedEmail}. They can sign in on the web autodialer and the desktop app with that email.`);
      setForm({ ...form, email: "", emailConfirm: "", password: "", fullName: "", sipExtension: "" });
      setCreateOpen(false);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Create failed");
    } finally {
      setBusy(false);
    }
  }

  async function onSaveStaff(row: StaffRecord) {
    if (!row.userId) return;
    const edit = staffEdits[row.userId] || staffEditFromRecord(row);
    const serviceContexts = serviceContextsFromValue(edit.service);
    const outboundNumberIds = edit.outboundNumberIds.filter((id) => {
      const number = numbers.find((n) => n.id === id);
      return number ? (number.serviceContexts || []).some((ctx) => serviceContexts.includes(ctx)) : false;
    });
    setMsg("");
    setError("");
    setBusy(true);
    try {
      await api.updateStaff(
        row.userId,
        {
          role: edit.role,
          serviceContexts,
          outboundNumberIds,
          desktopAccess: edit.desktopAccess,
          sipExtension: edit.sipExtension.trim(),
          forwardingNumber: edit.forwardingNumber.trim(),
          forwardingNumberVerified: edit.forwardingNumberVerified,
          forwardingEnabled: edit.forwardingEnabled,
          forwardingConfirmationRequired: edit.forwardingConfirmationRequired
        },
        csrf
      );
      setMsg(`Updated ${row.email}.`);
      setStaffEdits((prev) => ({ ...prev, [row.userId]: edit }));
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed");
    } finally {
      setBusy(false);
    }
  }

  async function onStaffAccess(row: StaffRecord, action: "revoke" | "restore") {
    if (!row.userId) return;
    const text =
      action === "revoke"
        ? `Deactivate Autodialer access for ${row.email}? They will stop receiving new routed calls.`
        : `Activate Autodialer access for ${row.email}?`;
    if (!window.confirm(text)) return;
    setMsg("");
    setError("");
    setBusy(true);
    try {
      if (action === "revoke") {
        const result = await api.revokeStaff(row.userId, csrf);
        setMsg(result.busy ? `Deactivated ${row.email}. They had an active call, so new routing is blocked while the call is allowed to finish.` : `Deactivated ${row.email}.`);
      } else {
        await api.restoreStaff(row.userId, csrf);
        setMsg(`Activated ${row.email}.`);
      }
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Access update failed");
    } finally {
      setBusy(false);
    }
  }

  async function onDeleteStaff(row: StaffRecord) {
    if (!row.userId || !window.confirm(`Delete the CRM staff assignment for ${row.email}? Their central login identity will be retained but CRM access will be deactivated.`)) return;
    setBusy(true); setError(""); setMsg("");
    try {
      await api.deleteStaff(row.userId, csrf);
      setMsg(`Deleted CRM agent assignment for ${row.email}.`);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    } finally { setBusy(false); }
  }

  async function onMegaReserveAccess(row: StaffRecord, active: boolean) {
    if (!row.userId) return;
    if (!window.confirm(`${active ? "Activate" : "Deactivate"} Mega Reserve agent access for ${row.email}?`)) return;
    setBusy(true); setError(""); setMsg("");
    try {
      const result = await api.setStaffServiceAccess(row.userId, "MEGAFED", active, csrf);
      setMsg(result.busy && !active
        ? `Mega Reserve access removed for ${row.email}; the active call may finish, but no new Mega Reserve calls will be offered.`
        : `Mega Reserve agent access ${active ? "activated" : "deactivated"} for ${row.email}.`);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Mega Reserve access update failed");
    } finally { setBusy(false); }
  }

  async function generateHoldMusic() {
    setMsg("");
    setError("");
    setHoldBusy("generate");
    try {
      await api.generateHoldMusic(holdForm, csrf);
      setMsg("Hold music generated. Preview it before activating.");
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Generation failed");
    } finally {
      setHoldBusy("");
    }
  }

  async function uploadHoldMusic() {
    if (!uploadFile) {
      setError("Choose an audio file first.");
      return;
    }
    setMsg("");
    setError("");
    setHoldBusy("upload");
    try {
      const body = new FormData();
      body.append("serviceContext", holdForm.serviceContext);
      body.append("prompt", holdForm.prompt);
      body.append("durationMs", String(holdForm.durationMs));
      body.append("instrumental", String(holdForm.instrumental));
      body.append("file", uploadFile);
      await api.uploadHoldMusic(body, csrf);
      setMsg("Hold music uploaded. Preview it before activating.");
      setUploadFile(null);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setHoldBusy("");
    }
  }

  async function activateHoldTrack(track: HoldMusicTrack) {
    setMsg("");
    setError("");
    setHoldBusy(track.id);
    try {
      await api.activateHoldMusic(track.serviceContext, track.id, csrf);
      setMsg(`Activated hold music for ${serviceLabel(track.serviceContext)}.`);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Activate failed");
    } finally {
      setHoldBusy("");
    }
  }

  async function disableHoldMusic(service: string) {
    if (!window.confirm(`Disable active hold music for ${serviceLabel(service)}?`)) return;
    setMsg("");
    setError("");
    setHoldBusy(`disable-${service}`);
    try {
      await api.disableHoldMusic(service, csrf);
      setMsg(`Disabled active hold music for ${serviceLabel(service)}.`);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Disable failed");
    } finally {
      setHoldBusy("");
    }
  }

  function updateStaffEdit(userId: string, patch: Partial<StaffEdit>) {
    setStaffEdits((prev) => ({
      ...prev,
      [userId]: { ...(prev[userId] || { role: "agent", service: "MEGAMILLIONSCLAIMS", desktopAccess: true, sipExtension: "", outboundNumberIds: [], forwardingNumber: "", forwardingNumberVerified: false, forwardingEnabled: true, forwardingConfirmationRequired: true }), ...patch }
    }));
  }

  function toggleOutboundNumber(userId: string, numberId: string, checked: boolean) {
    const row = staff.find((s) => s.userId === userId);
    const current = staffEdits[userId] || (row ? staffEditFromRecord(row) : { role: "agent", service: "MEGAMILLIONSCLAIMS", desktopAccess: true, sipExtension: "", outboundNumberIds: [], forwardingNumber: "", forwardingNumberVerified: false, forwardingEnabled: true, forwardingConfirmationRequired: true });
    const selected = new Set(current.outboundNumberIds || []);
    if (checked) selected.add(numberId);
    else selected.delete(numberId);
    updateStaffEdit(userId, { outboundNumberIds: [...selected] });
  }

  function numberLabel(number: NumberRecord) {
    const service = (number.serviceContexts || []).join(", ");
    return `${number.label || number.e164} (${number.e164})${service ? ` · ${service}` : ""}`;
  }

  function serviceLabel(value: string) {
    return HOLD_SERVICE_OPTIONS.find((option) => option.value === value)?.label || value;
  }

  if (!data && !error) return <p className="muted">Loading admin overview…</p>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Admin control</h1>
          <p>Create agent accounts for the web autodialer and Windows desktop. Same email and password on both.</p>
        </div>
      </div>

      {error ? <div className="alert error">{error}</div> : null}
      {msg ? <div className="alert">{msg}</div> : null}

      <div className="row-actions" style={{ marginBottom: "1rem" }}>
        <Link className="btn ghost" to="/admin/telephony">
          Telephony
        </Link>
        <button
          className="btn primary"
          type="button"
          onClick={() => {
            setError("");
            setCreateOpen(true);
          }}
        >
          + Create agent / supervisor
        </button>
      </div>

      {createOpen ? (
        <div
          className="admin-modal-backdrop"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !busy) setCreateOpen(false);
          }}
        >
          <div
            className="admin-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-staff-title"
          >
            <div className="admin-modal-head">
              <h2 id="create-staff-title">Create agent / supervisor</h2>
              <button
                className="btn ghost sm"
                type="button"
                disabled={busy}
                onClick={() => setCreateOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <p className="muted">
              Password must be 12+ characters with uppercase, lowercase, and a number.
            </p>

            <form onSubmit={onCreate} className="stack">
              <label>
                Full name
                <input
                  value={form.fullName}
                  onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                  placeholder="Agent name"
                />
              </label>

              <label>
                Email
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="agent@company.com"
                />
              </label>

              <label>
                Confirm email
                <input
                  type="email"
                  required
                  value={form.emailConfirm}
                  onChange={(e) => setForm({ ...form, emailConfirm: e.target.value })}
                  placeholder="agent@company.com"
                />
              </label>

              <label>
                Temporary password
                <input
                  type="password"
                  required
                  minLength={12}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
              </label>

              <label>
                Access level
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                >
                  <option value="agent">agent</option>
                  <option value="supervisor">supervisor</option>
                  <option value="admin">admin</option>
                </select>
              </label>

              <label>
                Service
                <select
                  value={form.service}
                  onChange={(e) => setForm({ ...form, service: e.target.value })}
                >
                  {SERVICE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                SIP extension
                <input
                  inputMode="numeric"
                  pattern="10[0-9]{2}"
                  value={form.sipExtension}
                  onChange={(e) => setForm({ ...form, sipExtension: e.target.value })}
                  placeholder="1000"
                />
              </label>

              <div className="row-actions" style={{ justifyContent: "flex-end" }}>
                <button
                  className="btn ghost"
                  type="button"
                  disabled={busy}
                  onClick={() => setCreateOpen(false)}
                >
                  Cancel
                </button>

                <button className="btn primary" disabled={busy} type="submit">
                  {busy ? "Creating…" : "Create account"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      <div className="card">
        <h2>Campaign ownership &amp; transfers</h2>
        <p className="muted">Owned campaigns: {routing.campaigns.length} · Active transfers: {routing.transfers.filter((t) => ["pending", "ringing", "accepted"].includes(String(t.transferResult || ""))).length} · Routing failures: {routing.failures.length}</p>
        <table>
          <thead><tr><th>Campaign</th><th>Owner</th><th>Status</th><th>Destination</th><th>Dispatched</th></tr></thead>
          <tbody>{routing.campaigns.slice(0, 50).map((c) => <tr key={String(c.id)}><td>{String(c.name || c.id)}</td><td className="mono">{String(c.ownerAgentId || "")}</td><td>{String(c.status || "")}</td><td>{String(c.receivingMethod || "not dispatched")}</td><td>{c.dispatchedAt ? new Date(String(c.dispatchedAt)).toLocaleString() : "—"}</td></tr>)}</tbody>
        </table>
      </div>

      <div className="card">
        <h2>Staff</h2>
        {staff.length === 0 ? (
          <p className="muted">No desktop staff records yet.</p>
        ) : (
          <table>
            <thead>
              <tr>
	                <th>Email</th>
	                <th>Access</th>
	                <th>Outbound numbers</th>
	                <th>Services</th>
	                <th>SIP extension</th>
	                <th>Personal phone</th>
	                <th>Desktop</th>
                        <th>Status</th>
	                <th></th>
              </tr>
            </thead>
            <tbody>
              {staff.map((s) => {
                const edit = staffEdits[s.userId] || staffEditFromRecord(s);
                const serviceContexts = serviceContextsFromValue(edit.service);
                return (
                  <tr key={s.userId || s.email}>
                    <td>{s.email || ""}</td>
                    <td>
                      <select value={edit.role} onChange={(e) => updateStaffEdit(s.userId, { role: e.target.value })}>
                        <option value="agent">agent</option>
                        <option value="supervisor">supervisor</option>
                        <option value="admin">admin</option>
                      </select>
                    </td>
                    <td>
                      {numbers.length === 0 ? (
                        <span className="muted">No numbers configured</span>
                      ) : (
                        <details className="outbound-dropdown">
                          <summary>
                            {(edit.outboundNumberIds || []).length === 0
                              ? "No outbound number"
                              : (edit.outboundNumberIds || []).length === 1
                                ? "1 number selected"
                                : `${(edit.outboundNumberIds || []).length} numbers selected`}
                          </summary>

                          <div className="outbound-dropdown-menu">
                            {numbers.map((number) => {
                              const compatible = (number.serviceContexts || []).some((ctx) =>
                                serviceContexts.includes(ctx)
                              );
                              const outbound =
                                (number.directions || ["outbound"]).includes("outbound") &&
                                (number.usages || ["campaign"]).includes("agent");
                              const assignable =
                                compatible &&
                                outbound &&
                                number.enabled !== false;

                              return (
                                <label
                                  key={number.id}
                                  className={!assignable ? "outbound-option disabled" : "outbound-option"}
                                >
                                  <input
                                    type="checkbox"
                                    checked={(edit.outboundNumberIds || []).includes(number.id)}
                                    disabled={!assignable}
                                    onChange={(e) =>
                                      toggleOutboundNumber(
                                        s.userId,
                                        number.id,
                                        e.target.checked
                                      )
                                    }
                                    aria-label={`Assign ${number.e164} to ${s.email}`}
                                  />

                                  <span>
                                    {numberLabel(number)}
                                    {!number.enabled
                                      ? " · disabled"
                                      : !outbound
                                        ? " · not agent outbound"
                                        : ""}
                                    {!compatible ? " · wrong service" : ""}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </details>
                      )}
                    </td>
                    <td>
                      <select value={edit.service} onChange={(e) => updateStaffEdit(s.userId, { service: e.target.value })}>
                        {SERVICE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </td>
	                    <td>
	                      <input
                        inputMode="numeric"
                        pattern="10[0-9]{2}"
                        value={edit.sipExtension}
                        onChange={(e) => updateStaffEdit(s.userId, { sipExtension: e.target.value })}
                        placeholder="1000"
                        style={{ maxWidth: 110 }}
                      />
                    </td>
                    <td>
                      <input value={edit.forwardingNumber} onChange={(e) => updateStaffEdit(s.userId, { forwardingNumber: e.target.value, forwardingNumberVerified: false })} placeholder="+1XXXXXXXXXX" style={{ maxWidth: 165 }} />
                      <label className="muted"><input type="checkbox" checked={edit.forwardingNumberVerified} onChange={(e) => updateStaffEdit(s.userId, { forwardingNumberVerified: e.target.checked })} /> admin verified</label>
                      <label className="muted"><input type="checkbox" checked={edit.forwardingEnabled} onChange={(e) => updateStaffEdit(s.userId, { forwardingEnabled: e.target.checked })} /> enabled</label>
                      <label className="muted"><input type="checkbox" checked={edit.forwardingConfirmationRequired} onChange={(e) => updateStaffEdit(s.userId, { forwardingConfirmationRequired: e.target.checked })} /> press-1 confirm</label>
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={edit.desktopAccess}
                        onChange={(e) => updateStaffEdit(s.userId, { desktopAccess: e.target.checked })}
                        aria-label={`Desktop access for ${s.email}`}
	                      />
	                    </td>
                    <td>
                      {s.desktopAccess === false ? (
                        <span className="badge failed">Deactivated</span>
                      ) : s.connectionStatus === "ONLINE" ? (
                        <span className="badge active">Online · {s.agentStatus || "AVAILABLE"}</span>
                      ) : (
                        <span className="badge">Offline</span>
                      )}
	                    </td>
	                    <td>
	                      <div className="row-actions">
		                        <button className="btn sm" disabled={busy || !s.userId} type="button" onClick={() => onSaveStaff(s)}>Save</button>
		                        {(s.serviceContexts || []).includes("MEGAFED") ? (
		                          <button className="btn sm danger" disabled={busy || !s.userId} type="button" onClick={() => onMegaReserveAccess(s, false)}>Deactivate Mega Reserve</button>
		                        ) : (
		                          <button className="btn sm success" disabled={busy || !s.userId} type="button" onClick={() => onMegaReserveAccess(s, true)}>Activate Mega Reserve</button>
		                        )}
	                        {s.desktopAccess === false ? (
	                          <button className="btn sm success" disabled={busy || !s.userId} type="button" onClick={() => onStaffAccess(s, "restore")}>Activate</button>
	                        ) : (
	                          <button className="btn sm danger" disabled={busy || !s.userId} type="button" onClick={() => onStaffAccess(s, "revoke")}>Deactivate</button>
	                        )}
	                        <button className="btn sm danger" disabled={busy || !s.userId} type="button" onClick={() => onDeleteStaff(s)}>Delete</button>
	                      </div>
	                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <div className="section-title">
          <h2>Hold Music</h2>
          <button className="btn sm ghost" type="button" onClick={load}>Refresh</button>
        </div>
        <div className="form-grid">
          <label>
            Service
            <select value={holdForm.serviceContext} onChange={(e) => setHoldForm({ ...holdForm, serviceContext: e.target.value })}>
              {HOLD_SERVICE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label>
            Duration
            <select value={holdForm.durationMs} onChange={(e) => setHoldForm({ ...holdForm, durationMs: Number(e.target.value) })}>
              <option value={15000}>15 seconds</option>
              <option value={30000}>30 seconds</option>
              <option value={60000}>60 seconds</option>
              <option value={120000}>2 minutes</option>
            </select>
          </label>
          <label className="inline-check">
            <input
              type="checkbox"
              checked={holdForm.instrumental}
              onChange={(e) => setHoldForm({ ...holdForm, instrumental: e.target.checked })}
            />
            Instrumental
          </label>
        </div>
        <label>
          Music Prompt
          <textarea value={holdForm.prompt} onChange={(e) => setHoldForm({ ...holdForm, prompt: e.target.value })} />
        </label>
        <div className="row-actions">
          <button className="btn primary" type="button" disabled={Boolean(holdBusy)} onClick={generateHoldMusic}>
            {holdBusy === "generate" ? "Generating..." : "Generate with ElevenLabs"}
          </button>
          <label className="file-picker">
            <input type="file" accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} />
            <span>{uploadFile ? uploadFile.name : "Choose audio"}</span>
          </label>
          <button className="btn ghost" type="button" disabled={Boolean(holdBusy) || !uploadFile} onClick={uploadHoldMusic}>
            {holdBusy === "upload" ? "Uploading..." : "Upload"}
          </button>
          <button className="btn danger" type="button" disabled={Boolean(holdBusy)} onClick={() => disableHoldMusic(holdForm.serviceContext)}>
            Disable active
          </button>
        </div>
        <table className="spaced-table">
          <thead>
            <tr>
              <th>Service</th>
              <th>Track</th>
              <th>Source</th>
              <th>Status</th>
              <th>Preview</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {holdTracks.length === 0 ? (
              <tr><td colSpan={6} className="muted">No hold music tracks yet.</td></tr>
            ) : (
              holdTracks.map((track) => (
                <tr key={track.id}>
                  <td>{serviceLabel(track.serviceContext)}</td>
                  <td>
                    <div className="mono">{track.id.slice(0, 8)}</div>
                    <div className="muted">{track.durationMs ? `${Math.round(track.durationMs / 1000)}s` : ""} {track.bytes ? ` · ${Math.round(track.bytes / 1024)} KB` : ""}</div>
                  </td>
                  <td>{track.source}</td>
                  <td>{track.active ? <span className="badge active">Active</span> : track.disabledAt ? <span className="badge stopped">Disabled</span> : <span className="badge draft">Preview</span>}</td>
                  <td>{track.previewUrl ? <audio controls src={track.previewUrl} preload="none" /> : null}</td>
                  <td>
                    <button className="btn sm success" type="button" disabled={Boolean(holdBusy)} onClick={() => activateHoldTrack(track)}>
                      {holdBusy === track.id ? "Activating..." : "Activate"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {data ? (
        <>
          <div className="grid-stats">
            <div className="stat-card">
              <div className="label">Users with settings</div>
              <div className="value">{data.usersWithSettings}</div>
            </div>
            <div className="stat-card">
              <div className="label">Contacts</div>
              <div className="value">{data.contacts}</div>
            </div>
            <div className="stat-card">
              <div className="label">Campaigns</div>
              <div className="value">{data.campaigns}</div>
            </div>
            <div className="stat-card">
              <div className="label">Call logs</div>
              <div className="value">{data.callLogs}</div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
